import { Link } from "react-router-dom";
import { FileText, MessageCircle, Wallet, Check, ArrowUpRight } from "lucide-react";

const SERVICES = [
    {
        key: "form-filling",
        title: "Form Filling",
        desc: "Online application and form submission services with expert accuracy across government and private portals.",
        icon: FileText,
        features: [
            "Bank Forms",
            "Aadhaar Forms",
            "Pan Forms",
            "Cutomized Forms",
        ],
        accent: "from-[#3BC7FF] to-[#7B61FF]",
    },
    {
        key: "whatsapp-bulk",
        title: "WhatsApp Bulk Messaging",
        desc: "Bulk promotional and transactional WhatsApp messaging solutions — reach thousands in minutes, safely.",
        icon: MessageCircle,
        features: [
            "Verified sender profiles",
            "Campaign scheduling &amp; templates",
            "Delivery &amp; read analytics",
            "Contact list segmentation",
        ],
        accent: "from-[#7B61FF] to-[#00D4FF]",
    },
    {
        key: "money-transfer",
        title: "Money Transfer Distributor",
        desc: "Authorised distributor for domestic money transfer services with real-time settlements and low commissions.",
        icon: Wallet,
        features: [
            "IMPS / NEFT / UPI settlements",
            "Retailer onboarding &amp; KYC",
            "Real-time transaction dashboard",
            "24×7 support &amp; reconciliation",
        ],
        accent: "from-[#00D4FF] to-[#3BC7FF]",
    },
];

export default function ServicesPage() {
    return (
        <section
            className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
            data-testid="services-page"
        >
            {/* Hero */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div>
                    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass-card text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                        Our Services
                    </span>
                    <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                        Everything you need,
                        <br />
                        <span className="text-gradient-brand">under one roof.</span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
                        From your first form to your first thousand transactions — Smart
                        Solutions blends people, process and technology to keep things
                        simple.
                    </p>
                </div>
                <div className="relative">
                    <div className="glass-card rounded-3xl p-6 lg:p-8">
                        <div className="grid grid-cols-3 gap-4">
                            {SERVICES.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <div
                                        key={s.key}
                                        className={`aspect-square rounded-2xl bg-gradient-to-br ${s.accent} p-[1.5px]`}
                                    >
                                        <div className="h-full w-full rounded-[14px] bg-background/80 backdrop-blur flex flex-col items-center justify-center gap-2 px-2 text-center">
                                            <Icon className="h-6 w-6" strokeWidth={1.8} />
                                            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                                {s.title.split(" ")[0]}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-6 flex items-center justify-between">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                    Uptime
                                </div>
                                <div className="font-display font-bold text-xl">99.9%</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                    Clients
                                </div>
                                <div className="font-display font-bold text-xl">1k+</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                    Rating
                                </div>
                                <div className="font-display font-bold text-xl">4.9/5</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service cards */}
            <div
                className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6"
                data-testid="services-list"
            >
                {SERVICES.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <article
                            key={s.key}
                            data-testid={`service-card-${s.key}`}
                            className="relative glass-card rounded-3xl p-6 lg:p-7 hover-lift overflow-hidden fade-in-up flex flex-col"
                            style={{ animationDelay: `${120 * i}ms` }}
                        >
                            <div
                                className={`pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-gradient-to-br ${s.accent} opacity-25 blur-3xl`}
                                aria-hidden="true"
                            />
                            <div className={`relative h-12 w-12 rounded-2xl bg-gradient-to-br ${s.accent} p-[1.5px]`}>
                                <div className="h-full w-full rounded-[14px] bg-background/80 backdrop-blur flex items-center justify-center">
                                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                                </div>
                            </div>
                            <h3 className="relative mt-4 text-xl font-display font-semibold tracking-tight">
                                {s.title}
                            </h3>
                            <p className="relative mt-2 text-sm text-muted-foreground leading-relaxed">
                                {s.desc}
                            </p>
                            <ul className="relative mt-4 space-y-2">
                                {s.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm">
                                        <span className={`mt-0.5 h-4 w-4 rounded-full bg-gradient-to-br ${s.accent} flex items-center justify-center shrink-0`}>
                                            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                        </span>
                                        <span dangerouslySetInnerHTML={{ __html: f }} />
                                    </li>
                                ))}
                            </ul>
                            <div className="relative mt-6 pt-4 border-t border-border/60 flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                    Available now
                                </span>
                                <Link
                                    to="/contact"
                                    data-testid={`service-cta-${s.key}`}
                                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full btn-gradient ripple text-white text-xs font-semibold"
                                >
                                    Enquire <ArrowUpRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}
